import Markdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  Box,
  Code,
  Em,
  Link,
  ListItem,
  ListRoot,
  Separator,
  Strong,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpRightFromSquareRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

/**
 * 原稿のリンクがプロダクトの外 (別オリジンの http / https) を指すか。
 * 相対パス・同一オリジンはプロダクトの中の画面として扱う。
 */
const isExternalHref = (href: string | undefined): boolean => {
  if (href === undefined || href === '') return false
  try {
    const url = new URL(href, window.location.origin)
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.origin !== window.location.origin
    )
  } catch {
    return false
  }
}

const components: Components = {
  // 見出しは画面側が index の title で描く。原稿の H1 は title と同じ 1 行
  // (announcement-policy.md §4) で、描くと二重表示になり、index で訂正された
  // title とも食い違うため描画しない (Design Note D-UI-2)。
  h1: () => null,
  h2: ({ children }) => (
    <Text as="h2" textStyle="sectionTitle" marginTop={6} marginBottom={2}>
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text as="h3" textStyle="subSectionTitle" marginTop={4} marginBottom={2}>
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text as="h4" textStyle="subSectionTitle" marginTop={3} marginBottom={1}>
      {children}
    </Text>
  ),
  h5: ({ children }) => (
    <Text as="h5" textStyle="subSectionTitle" marginTop={3} marginBottom={1}>
      {children}
    </Text>
  ),
  h6: ({ children }) => (
    <Text as="h6" textStyle="subSectionTitle" marginTop={3} marginBottom={1}>
      {children}
    </Text>
  ),
  p: ({ children }) => (
    <Text textStyle="body" marginBottom={2}>
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <ListRoot as="ul" marginBottom={2} paddingStart={5} listStyleType="disc">
      {children}
    </ListRoot>
  ),
  ol: ({ children }) => (
    <ListRoot as="ol" marginBottom={2} paddingStart={5} listStyleType="decimal">
      {children}
    </ListRoot>
  ),
  li: ({ children }) => <ListItem textStyle="body">{children}</ListItem>,
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Em>{children}</Em>,
  code: ({ children }) => <Code size="sm">{children}</Code>,
  pre: ({ children }) => (
    <Box as="pre" overflowX="auto" marginBottom={2}>
      {children}
    </Box>
  ),
  // href は react-markdown の既定 urlTransform を通った後の値 (`javascript:` 等は
  // 空文字に落ちている)。urlTransform を上書きするとこの無害化が外れるので渡さない。
  // 原稿のリンク先はヘルプセンター等の外部サイトが主で、プロダクトの外へのリンクは
  // 新しいタブ + external icon、 プロダクトの中の画面へのリンクは同じタブで開く
  // (2026-10-16 デザイナー決定、 [[isExternalHref]])。
  a: ({ href, children }) =>
    isExternalHref(href) ? (
      <Link href={href} rel="noopener noreferrer" target="_blank">
        {children}
        <FaArrowUpRightFromSquareRegular
          boxSize={iconSize.externalLink}
          aria-hidden="true"
        />
      </Link>
    ) : (
      <Link href={href}>{children}</Link>
    ),
  // 画像は描画しない。原稿規約 (announcement-policy.md §4) に画像は無く、GCS 上で
  // 手動訂正された原稿に外部 URL の画像が入ると、詳細を開いた全ユーザーのブラウザが
  // その URL へ GET を送り閲覧の事実が外部に漏れる (frontend に img-src の CSP は無い)。
  img: () => null,
  hr: () => <Separator marginY={4} />,
  blockquote: ({ children }) => (
    <Box
      as="blockquote"
      marginBottom={2}
      paddingStart={3}
      color="Text.midEmphasis"
      borderColor="Border.lowEmphasis"
      borderStartWidth="4px"
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box overflowX="auto" marginBottom={2}>
      <TableRoot size="sm">{children}</TableRoot>
    </Box>
  ),
  thead: ({ children }) => <TableHeader>{children}</TableHeader>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableColumnHeader>{children}</TableColumnHeader>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
}

type Props = {
  /** 告知原稿 (docs/releases/announcement-policy.md §4 の markdown)。 */
  markdown: string
}

/**
 * お知らせ本文の markdown を DS の typography で描画する。
 *
 * 生 HTML は要素化しない (rehype-raw を足さない。react-markdown は既定で raw HTML を
 * テキストとして出す)。原稿は GCS 上で手動訂正されうる入力で、HTML 文字列を注入する
 * 経路 (backend HTML 化 + dangerouslySetInnerHTML) は XSS 面を増やすため採らない
 * (Design Note D-UI-3)。
 */
export const AnnouncementMarkdown = ({
  markdown,
}: Props): React.ReactElement => (
  <Box>
    <Markdown components={components} remarkPlugins={[remarkGfm]}>
      {markdown}
    </Markdown>
  </Box>
)
